/**
 * Application version configuration
 * Version format: YY.M.D or YY.M.D.BUILD
 * 
 * This file exports the current application version which is injected
 * at build time from package.json via Vite's define configuration.
 */

export const APP_VERSION: string = __APP_VERSION__ || '0.0.0';

/**
 * Version information type
 */
export type VersionT = {
  year: string;
  month: string;
  day: string;
  build: string | undefined;
  full: string;
};

/**
 * Parse version string into components
 */
export function parseVersion(version: string = APP_VERSION): VersionT {
  const parts = version.split('.');
  return {
    year: parts[0] || '0',
    month: parts[1] || '0',
    day: parts[2] || '0',
    build: parts[3] || undefined,
    full: version
  };
}

/**
 * Get formatted version date
 */
export function getVersionDate(version: string = APP_VERSION): Date {
  const { year, month, day } = parseVersion(version);
  
  // Convert YY to YYYY (assuming 20YY for years 00-99)
  const fullYear = 2000 + parseInt(year, 10);
  
  // Month is 0-indexed in Date constructor
  return new Date(fullYear, parseInt(month, 10) - 1, parseInt(day, 10));
}

/**
 * Check if current version is from today
 */
export function isVersionFromToday(): boolean {
  const versionDate = getVersionDate();
  const today = new Date();
  
  return (
    versionDate.getFullYear() === today.getFullYear() &&
    versionDate.getMonth() === today.getMonth() &&
    versionDate.getDate() === today.getDate()
  );
}

/**
 * Log version information to console
 */
export function logVersionInfo(): void {
  const { year, month, day, build, full } = parseVersion();
  const versionDate = getVersionDate();
  
  console.log(
    `%c🚀 Baseworks v${full}`,
    'color: #6366f1; font-weight: bold; font-size: 14px;'
  );
  
  console.log(
    `%c📅 Version Date: ${versionDate.toLocaleDateString()}${build ? ` (Build ${build})` : ''}`,
    'color: #8b5cf6; font-size: 12px;'
  );
  
  if (isVersionFromToday()) {
    console.log(
      '%c✨ Running latest version from today!',
      'color: #10b981; font-size: 12px;'
    );
  }
}

// Export version components for convenience
const parsedVersion = parseVersion();

export const VERSION_YEAR = parsedVersion.year;
export const VERSION_MONTH = parsedVersion.month;
export const VERSION_DAY = parsedVersion.day;
export const VERSION_BUILD = parsedVersion.build;